import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../components/ui/Button';
import { useEntityMeta } from './useEntityMeta';
import { DefaultFieldWidget, DevWarningBox, RelationWidget, toFieldDescriptor } from './widgets';
import { useSeedEffect } from '../hooks/useSeedEffect';
import type { MetaField, MetaRelation, SchemaFormProps } from './types';
import './SchemaForm.css';

/**
 * Schema-driven form generator.
 *
 * The entity meta (fetched from GET /api/meta/:entity) declares facts about
 * each field; the page declares sections of field NAMES; overrides and
 * visibility predicates are surgical. String items resolve against meta.fields
 * first, then meta.relations (m2m). Custom React nodes are rendered inline.
 */
const SchemaForm: React.FC<SchemaFormProps> = ({
  entity,
  sections,
  overrides,
  visibility,
  relationOptions,
  enumLabelKeys,
  requiredOverrides,
  validate,
  initialValues,
  seedKey,
  onSubmit,
  onValuesChange,
  footer,
  secondaryAction,
  submitLabelKey = 'common.save',
  submitLabel,
  loading,
}) => {
  const { t } = useTranslation();
  const { meta, loading: metaLoading, error: metaError } = useEntityMeta(entity);

  const [values, setValues] = useState<Record<string, any>>(initialValues ?? {});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Seeded once per record (seedKey), else per initialValues identity.
  useSeedEffect(initialValues, seedKey, (seed) => {
    setValues(seed ?? {});
    setErrors({});
  });

  // Notify the parent of value changes (for change detection in edit pages).
  // onValuesChange is intentionally excluded from deps to avoid re-firing when a
  // parent passes a non-memoized callback (matches the legacy form pattern).
  useEffect(() => {
    if (onValuesChange) onValuesChange(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const fieldMap = useMemo(() => {
    const map = new Map<string, MetaField>();
    meta?.fields.forEach((f) => map.set(f.name, f));
    return map;
  }, [meta]);

  const relationMap = useMemo(() => {
    const map = new Map<string, MetaRelation>();
    meta?.relations.forEach((r) => map.set(r.name, r));
    return map;
  }, [meta]);

  const setValue = (name: string, value: any) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!meta) return;

    // Client-side required validation, mirroring MemberForm. Only validates
    // fields actually present in the rendered sections.
    const newErrors: Record<string, string> = {};
    const renderedNames = new Set<string>();
    sections.forEach((section) => {
      section.items.forEach((item) => {
        if (typeof item === 'string') renderedNames.add(item);
      });
    });

    for (const name of renderedNames) {
      // Skip fields hidden by a visibility predicate.
      if (visibility?.[name] && !visibility[name](values)) continue;
      const field = fieldMap.get(name);
      const isRequired = (field?.required ?? false) || Boolean(requiredOverrides?.[name]);
      if (!field || !isRequired) continue;
      const v = values[name];
      const empty =
        v === undefined ||
        v === null ||
        (typeof v === 'string' && v.trim() === '') ||
        (Array.isArray(v) && v.length === 0);
      if (empty) newErrors[name] = t('validation.required');
    }

    // Per-field validators run for every rendered field, required or not — a
    // structured field can be non-empty and still incomplete.
    if (validate) {
      for (const name of renderedNames) {
        if (newErrors[name]) continue;
        if (visibility?.[name] && !visibility[name](values)) continue;
        const validator = validate[name];
        if (!validator) continue;
        // The MERGED descriptor, not raw meta: a field made required via
        // `requiredOverrides` renders an asterisk, so its validator has to see
        // `required: true` as well or the form looks required and checks
        // nothing.
        const field = fieldMap.get(name);
        const message = validator(values[name], values, field && {
          ...field,
          required: field.required || Boolean(requiredOverrides?.[name]),
        });
        if (message) newErrors[name] = message;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(values);
  };

  const renderItem = (item: string | React.ReactNode, key: React.Key): React.ReactNode => {
    if (typeof item !== 'string') {
      return <React.Fragment key={key}>{item}</React.Fragment>;
    }

    const name = item;

    // Hidden by visibility predicate.
    if (visibility?.[name] && !visibility[name](values)) {
      return null;
    }

    const forcedRequired = Boolean(requiredOverrides?.[name]);

    // Per-field override wins regardless of kind.
    const Override = overrides?.[name];
    if (Override) {
      const field = fieldMap.get(name);
      const baseDescriptor = field
        ? toFieldDescriptor(field)
        : { name, kind: 'json', required: false, labelKey: `${entity}s.${name}` };
      const descriptor = forcedRequired ? { ...baseDescriptor, required: true } : baseDescriptor;
      return (
        <Override
          key={key}
          field={descriptor}
          value={values[name]}
          onChange={(v) => setValue(name, v)}
          error={errors[name]}
          values={values}
        />
      );
    }

    // Resolve against meta.fields, then meta.relations.
    const field = fieldMap.get(name);
    if (field) {
      // json/array/custom fields without an override → loud warning.
      if (field.kind === 'json' || field.kind === 'array' || field.widget === 'custom') {
        return (
          <DevWarningBox
            key={key}
            message={`field "${name}" (kind "${field.kind}"${field.widget ? `, widget "${field.widget}"` : ''}) requires an override.`}
          />
        );
      }
      const descriptor = forcedRequired
        ? { ...toFieldDescriptor(field), required: true }
        : toFieldDescriptor(field);
      return (
        <DefaultFieldWidget
          key={key}
          entity={entity}
          field={descriptor}
          value={values[name]}
          onChange={(v) => setValue(name, v)}
          error={errors[name]}
          enumLabelKeys={enumLabelKeys?.[name]}
          optionsFetcher={relationOptions?.[name]}
        />
      );
    }

    const relation = relationMap.get(name);
    if (relation) {
      return (
        <RelationWidget
          key={key}
          relation={relation}
          value={values[name] ?? []}
          onChange={(v) => setValue(name, v)}
          error={errors[name]}
          optionsFetcher={relationOptions?.[name]}
        />
      );
    }

    // Unknown name → loud dev warning, never silently skipped.
    return (
      <DevWarningBox key={key} message={`unknown field "${name}" — not found in meta.fields or meta.relations for entity "${entity}".`} />
    );
  };

  if (metaError) {
    return <DevWarningBox message={`failed to load meta for "${entity}": ${metaError}`} />;
  }

  if (metaLoading || !meta) {
    return <p>{t('common.loading')}</p>;
  }

  return (
    <form onSubmit={handleSubmit} className="form-container">
      {sections.map((section, sIdx) => (
        <div className="form-section schema-form__section" key={sIdx}>
          {section.titleKey && (
            <h4 className="form-section__title">{t(section.titleKey)}</h4>
          )}
          <div className={`schema-form__grid--${section.columns ?? 1}`}>
            {section.items.map((item, iIdx) => renderItem(item, `${sIdx}-${iIdx}`))}
          </div>
        </div>
      ))}

      {footer}

      <Button type="submit" fullWidth loading={loading}>
        {submitLabel ?? t(submitLabelKey)}
      </Button>

      {secondaryAction}
    </form>
  );
};

export default SchemaForm;
